import React, {createContext, useCallback, useState, useContext, ReactNode, useEffect} from 'react';
import api from '../services/api';
import {Alerta} from "../utils/Alerta";
import moment from "moment/moment";

interface SignInCredentials {
  email?: string;
  password?: string;
  id?: number;
  isMonitor?: boolean;
  newPassword?: string;
  codeTotp?: string;
}

interface SignInMonitorCredentials {
  email?: string;
  password?: string;
  secret?: string;
}

export interface StoreData {
  id?: number;
  email?: string;
  status?: number;
  name?: string;
  cnpj?: string;
  program_type?: number;
  platform_id?: number;
  isMonitor?: boolean;
  twoFactorAuthentication?: boolean;
  newPasswordRequired?: boolean;
  resetPasswordByInactive?: boolean;
  resetPasswordByTime?: boolean;
  needCreateTwoFactorAuth?: boolean;
}

export interface UserData {
    id?: number;
    username?: string;
    name?: string;
    master?: boolean;
    created_at?: string;
    updated_at?: string;
}

interface NewPasswordCredentials {
  email?: string;
  password?: string;
  code?: string;
}

interface AuthContextData {
  email: string;
  store: StoreData | null;
  user: UserData | null;
  expiration: string;
  signIn(credentials: SignInCredentials): Promise<boolean>;
  signInMonitor(credentials: SignInMonitorCredentials): Promise<boolean>;
  signOut(): void;
  passwordTmp: string;
  isAuth: boolean;
  resetPassword(email: string): Promise<boolean>;
  sendNewPassword(data: NewPasswordCredentials): Promise<boolean>;
}

interface AuthState {
  token?: string;
  expiration?: string;
  store?: StoreData | null;
  user?: UserData | null;
  passwordTmp?: string;
  email?: string;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [ isAuth, setAuth ] = useState<boolean>(false);
  let apiSender = api();
  const [data, setData] = useState<AuthState>(() => {
    const token = localStorage.getItem('@fidegg:token') || localStorage.getItem('@fidegg:tokenMonitor') || undefined;
    const expiration = localStorage.getItem('@fidegg:token-expiration') || localStorage.getItem('@fidegg:token-expiration-monitor') || undefined;
    const email = localStorage.getItem('@fidegg:email') || null;
    let store: any = localStorage.getItem('@fidegg:store') || null;
    if (store) {
      store = JSON.parse(store) as StoreData;
    }
    let user: any = localStorage.getItem('@fidegg:user') || null;
    if (user) {
        user = JSON.parse(user) as UserData;
    }

    if (expiration === undefined) {
      return {
        email: email,
      } as AuthState;
    }

    const date = moment();
    const expirationDate = moment(expiration)
    const expired = date.isAfter(expirationDate);

    if (token && !expired) {
      setAuth(true);
      apiSender.defaults.headers.authorization = `Bearer ${token}`;
      return {
        token,
        expiration,
        store: store,
        user: user,
      };
    }

    return {} as AuthState;
  });

  const signIn = useCallback(async ({
    email,
    password,
    id,
    isMonitor = false,
    newPassword,
    codeTotp,
  }: SignInCredentials) => {
    let response;
    if (isMonitor && id != null) {
      const tokenMonitor = localStorage.getItem('@fidegg:tokenMonitor');
      apiSender.defaults.headers.authorization = tokenMonitor ? `Bearer ${tokenMonitor}` : '';
      response = await apiSender.get('/storebyid', { params: { id } });
    } else {
      response = await apiSender.post('/auth', { email, password, newPassword, codeTotp });
    }

    const {
      token = null,
      store = null,
      expiration = null,
      newPasswordRequired = false,
      twoFactorAuthentication = false,
      resetPasswordByInactive = false,
      resetPasswordByTime = false,
      needCreateTwoFactorAuth = false,
    } = response.data;

    const validStore: StoreData = {
      ...(store || {}),
      newPasswordRequired,
      resetPasswordByInactive,
      resetPasswordByTime,
      twoFactorAuthentication,
      needCreateTwoFactorAuth,
      isMonitor,
    };

    if (resetPasswordByInactive) {
      Alerta('Acesso suspenso por inatividade. Verifique seu e-mail para redefinir a senha e restaurar o acesso.', 'info');
    }

    if (resetPasswordByTime) {
      Alerta('Sua senha expirou. Verifique seu e-mail para obter as instruções de redefinição e criar uma nova senha.', 'info');
    }

    // Cenários sem token (TOTP / troca de senha)
    if ((newPasswordRequired || twoFactorAuthentication || resetPasswordByInactive || resetPasswordByTime || needCreateTwoFactorAuth) && !token) {
      if (email != null) {
        localStorage.setItem('@fidegg:email', email);
      }

      setData(prev => ({ ...prev, store: validStore, passwordTmp: newPassword ?? password }));
      return true;
    }

    if (token) {
      setAuth(true);

      localStorage.removeItem('@fidegg:email');

      localStorage.setItem('@fidegg:token', token);
      localStorage.setItem('@fidegg:token-expiration', expiration);
      localStorage.setItem('@fidegg:store', JSON.stringify(validStore));

      apiSender.defaults.headers.authorization = `Bearer ${token}`;
      setData(prev => ({
        ...prev,
        token,
        expiration,
        store: validStore,
      }));
      return true;
    }

    return false;
  }, []);

  const signInMonitor = useCallback(async ({
    email,
    password,
    secret,
  }: SignInMonitorCredentials) => {
    const response = await apiSender.post('/auth/monitor', { email, password, secret });
    const {
      tokenMonitor,
      user,
      expirationToken,
      twofactorauthentication
    } = response.data;

    if (twofactorauthentication) return true;

    if (response.data?.error?.status == 202 && response.data.error.message.includes('A sua senha é muito antiga')) {
      return (response.data.error.message.split('ACCEPTED: ')[1]);
    }

    if (tokenMonitor) {
      setAuth(true);

      localStorage.setItem('@fidegg:tokenMonitor', tokenMonitor);
      localStorage.setItem('@fidegg:token-expiration-monitor', expirationToken);
      localStorage.setItem('@fidegg:user', JSON.stringify(user));

      apiSender.defaults.headers.authorization = `Bearer ${tokenMonitor}`;

      setData(prev => ({
        ...prev,
        store: user,
        user: user,
      }));
      return true;
    }

    return false;
  }, []);

  const signOut = useCallback(() => {
    setAuth(false);
    localStorage.removeItem('@fidegg:token');
    localStorage.removeItem('@fidegg:token-expiration');
    localStorage.removeItem('@fidegg:store');
    localStorage.removeItem('@fidegg:tokenMonitor');
    localStorage.removeItem('@fidegg:token-expiration-monitor');
    localStorage.removeItem('@fidegg:storeList');

    apiSender.defaults.headers.authorization = '';
    setData({});
  }, []);

  const resetPassword = (async (email: string) => {
    const data = {
      email : email ,
    }

    try {
      await apiSender.post("cognito/resetpassword", data);

      setData(prev => ({
        ...prev,
        email: email,
      }));

      localStorage.setItem('@fidegg:email', email);

      return true;
    } catch (e) {
      Alerta('Não foi possível enviar o código para criar uma nova senha.');
      return false;
    }
  });

  const sendNewPassword = (async (data: NewPasswordCredentials) => {
    try {
      await apiSender.post("cognito/passwordchange", data);

      return true;
    } catch (e) {
      return false;
    }
  });

  return (
    <AuthContext.Provider
      value={{
        email: localStorage.getItem('@fidegg:email') ?? data.email ?? '',
        store: data.store ?? null,
        user: data.user ?? null,
        expiration: data.expiration ?? '',
        signIn,
        signInMonitor,
        signOut,
        passwordTmp: data.passwordTmp || '',
        isAuth,
        resetPassword,
        sendNewPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
